import React from 'react';

import { parseCanvasSection } from './sections/canvas/canvasReaderModel.js';

const educationSectionComponents = {
  educationHero: 'EducationHeroSection',
  // Exam sections resolve to the auth bridge (EducationExamAuthBridge.jsx) so the nav can
  // show the candidate's account control once signed in.
  educationExamHome: 'EducationExamHomeSection',
  educationExamCourses: 'EducationExamPagesSection',
  educationExamCourseDetail: 'EducationExamPagesSection',
  educationExamAbout: 'EducationExamPagesSection',
  educationExamContact: 'EducationExamPagesSection',
  educationExamInfo: 'EducationExamPagesSection',
  educationExamShell: 'EducationExamShellSection',
  educationCategoryGrid: 'EducationCategoryGridSection',
  educationFeatureGrid: 'EducationFeatureGridSection',
  educationFeaturedCourses: 'EducationFeaturedCoursesSection',
  educationMissionVision: 'EducationMissionVisionSection',
  educationJoinCommunity: 'EducationJoinCommunitySection',
  educationInfoPage: 'EducationInfoPageSection',
  educationProductsPage: 'EducationProductsPageSection',
  educationCourseDetail: 'EducationCourseDetailPageSection',
  educationCart: 'EducationCartPageSection',
  educationSummary: 'EducationSummaryPageSection',
  educationAbout: 'EducationAboutPageSection',
  educationContact: 'EducationContactPageSection',
  educationAuthEntry: 'EducationAuthEntrySection',
  authSignIn: 'EducationAuthEntrySection',
  authSignUp: 'EducationAuthEntrySection'
};

const waasSectionComponents = {
  waasHero: 'WaasHeroSection',
  waasTrustedLogos: 'WaasTrustedLogosSection',
  waasAboutStory: 'WaasAboutStorySection',
  waasPhilosophy: 'WaasPhilosophySection',
  waasWhyAvps: 'WaasWhyAvpsSection',
  waasServicesGrid: 'WaasServicesGridSection',
  waasServiceDetail: 'WaasServiceDetailSection',
  waasStatsStrip: 'WaasStatsStripSection',
  waasProcessSteps: 'WaasProcessStepsSection',
  waasInstructorSpotlight: 'WaasInstructorSpotlightSection',
  waasSchedule: 'WaasScheduleSection',
  waasPricingPlans: 'WaasPricingPlansSection',
  waasTestimonials: 'WaasTestimonialsSection',
  waasFaq: 'WaasFaqSection',
  waasImageGallery: 'WaasImageGallerySection',
  waasCareerHero: 'WaasCareerHeroSection',
  waasOpenRoles: 'WaasOpenRolesSection',
  waasContact: 'WaasContactSection',
  waasAuth: 'WaasAuthSection',
  waasNewsletterFooter: 'WaasNewsletterFooterSection',
  waasPremiumHome: 'WaasPremiumHomeSection',
  waasAvpsInlinePage: 'WaasAvpsInlinePagesSection'
};

const flexOfficeSectionComponents = {
  flexOfficeHero: 'FlexOfficeHeroSection',
  flexOfficeAbout: 'FlexOfficeAboutSection',
  flexOfficeTimeline: 'FlexOfficeTimelineSection',
  flexOfficeWorkingHours: 'FlexOfficeWorkingHoursSection',
  flexOfficeFeatures: 'FlexOfficeFeaturesSection',
  flexOfficeCta: 'FlexOfficeCtaSection',
  flexOfficeServicesPage: 'FlexOfficeServicesPageSection',
  flexOfficeServiceDetail: 'FlexOfficeServiceDetailSection',
  flexOfficeBookingSearch: 'FlexOfficeBookingSearchSection',
  flexOfficeBookings: 'FlexOfficeBookingsSection',
  flexOfficeInvoice: 'FlexOfficeInvoiceSection',
  flexOfficeContact: 'FlexOfficeContactSection'
};

const genericSectionComponents = {
  featureGrid: 'FeatureGridSection',
  imageBanner: 'ImageBannerSection',
  faq: 'FAQSection',
  testimonials: 'TestimonialsSection',
  contactBlock: 'ContactBlockSection',
  customHtml: 'CustomHtmlSection',
  customCanvas: 'CanvasSectionRenderer'
};

const contentSectionTypes = new Set(['content', 'richText', 'productRail', 'cartSummary']);

/** Education sections that render real academy courses/categories from live data. */
const educationLiveDataSectionTypes = new Set([
  'educationCategoryGrid',
  'educationFeaturedCourses',
  'educationExamHome',
  'educationExamCourses'
]);

const renderNamedComponent = (components, componentName, props) => {
  const Component = components[componentName];
  return Component ? <Component {...props} /> : null;
};

const renderEducationSection = ({ components, educationData, section, template, theme }) => {
  if (section.type === 'educationHeaderRail') {
    return template?.attributes?.header
      ? null
      : renderNamedComponent(components, 'EducationDomainHeader', {
        productList: educationData.products || []
      });
  }

  const componentName = educationSectionComponents[section.type];
  if (!componentName) return undefined;

  const props = { section, theme };
  if (educationLiveDataSectionTypes.has(section.type)) {
    props.data = educationData;
  }

  return renderNamedComponent(components, componentName, props);
};

export const createTemplateSectionContentRenderer = ({ components, renderRetailSection }) => (
  ({ section, theme, template, educationData }) => {
    const sectionType = section?.type || '';

    // ── CANONICAL CANVAS GATE (Req 13.1–13.4) ────────────────────────────────
    // This is the SINGLE canonical path for Canvas_Builder payloads, and it MUST
    // run strictly BEFORE all type-keyed routing below — including the
    // `customCanvas → CanvasSectionRenderer` mapping in `genericSectionComponents`.
    //
    // Persisted Canva-style Editor sections carry a self-describing `section.canvas`
    // block while KEEPING their original `type`, so type-keyed routing below cannot
    // reach them. Intercepting here guarantees that:
    //   - WHEN parseCanvasSection(section) !== null, the section is rendered by
    //     EXACTLY ONE reader — CanvasSectionReader (Req 13.1); and
    //   - a section carrying a valid Canvas_Payload can NEVER reach the alternate
    //     `content.canvas.elements` renderer (CanvasSectionRenderer), because this
    //     gate returns before the `customCanvas` / type-keyed branches (Req 13.2).
    //
    // parseCanvasSection is a strict shape guard (top-level `canvas` block with a
    // valid `referenceWidth`). It returns `null` for a section with no canvas block,
    // or one whose payload is invalid/malformed (e.g. a bad `referenceWidth`); in
    // that case we DO NOT short-circuit, so the dispatcher falls through to its
    // normal type-keyed renderer (Req 13.3, 13.4). Legacy sections — including the
    // `customCanvas` type, whose data lives under `content.canvas.elements` rather
    // than a top-level `canvas` block — therefore pass through untouched and reach
    // their existing renderer below.
    if (parseCanvasSection(section) !== null) {
      return renderNamedComponent(components, 'CanvasSectionReader', { section, theme, template });
    }

    if (sectionType === 'existingComponent' || sectionType === 'legacyComponent') {
      return renderNamedComponent(components, 'ExistingComponentSection', { section, theme, template });
    }

    const educationSection = renderEducationSection({ components, educationData, section, template, theme });
    if (educationSection !== undefined) return educationSection;

    const waasComponentName = waasSectionComponents[sectionType];
    if (waasComponentName) {
      return renderNamedComponent(components, waasComponentName, { section, theme, template });
    }
    if (sectionType.startsWith('waas')) {
      return renderNamedComponent(components, 'GenericTemplateSection', { section, theme });
    }

    const flexComponentName = flexOfficeSectionComponents[sectionType];
    if (flexComponentName) {
      return renderNamedComponent(components, flexComponentName, { section, theme, template });
    }
    if (sectionType.startsWith('flexOffice')) {
      return renderNamedComponent(components, 'GenericTemplateSection', { section, theme });
    }

    if (sectionType.startsWith('retail')) {
      return renderRetailSection(section, theme);
    }

    if (sectionType.startsWith('education')) {
      return renderNamedComponent(components, 'EducationWrapperSection', { section, theme });
    }

    const genericComponentName = genericSectionComponents[sectionType];
    if (genericComponentName) {
      return renderNamedComponent(components, genericComponentName, { section, theme });
    }

    if (contentSectionTypes.has(sectionType)) {
      return renderNamedComponent(components, 'ContentSection', { section, theme });
    }

    if (sectionType && sectionType !== 'hero') {
      return renderNamedComponent(components, 'GenericTemplateSection', { section, theme });
    }

    return renderNamedComponent(components, 'HeroSection', { section, theme });
  }
);
